import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ClipboardList, History, List, MapPinned, Sparkles } from 'lucide-react';
import { authenticatedFetch } from '../../utils/api';
import { API_HOST } from '../../utils/constants';
import { getProjectEntryPath, isInteractiveGameProject } from '../../utils/projectRuntime';
import { useUser } from '../../context/UserContext';
import OperationsEntryLink from '../common/OperationsEntryLink';
import PixelStage from './PixelStage';
import AvatarCustomizer from './AvatarCustomizer';
import TownBoardPanel from './TownBoardPanel';
import { loadAvatar, makeDefaultAvatar } from './avatar';
import { buildLobbyScene } from './maps';
import { C } from './engine';
import {
  createTownProjectVisit, getTownContinuationLabel, loadTownContinuation,
  resolveTownContinuation, saveTownContinuation,
} from './townContinuation';

/** 像素风加载骨架：深色舞台 + 抖动色块 */
function PixelSkeleton() {
  const blocks = [
    { left: '18%', top: '22%', width: 120, height: 80 },
    { left: '44%', top: '22%', width: 120, height: 80 },
    { left: '70%', top: '22%', width: 120, height: 80 },
    { left: '10%', top: '62%', width: 72, height: 48 },
    { left: '78%', top: '62%', width: 72, height: 48 },
  ];
  return (
    <div className="w-full h-full relative" style={{ backgroundColor: C.wallDark }}>
      {blocks.map((b, i) => (
        <div key={i} className="skeleton absolute" style={{ ...b, opacity: 0.5, borderRadius: 2 }} />
      ))}
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-xs font-bold"
        style={{ color: C.cream, fontFamily: 'ui-monospace, Menlo, monospace' }}
      >
        正在生成小镇地图…
      </div>
    </div>
  );
}

/**
 * 像素办公室 · 大厅（首页 pixel 模式）
 * 拉取真实项目列表动态生成工位岛，走上门垫 Enter 进入项目房间。
 */
export default function PixelOfficePage() {
  const { userId, user } = useUser();
  const navigate = useNavigate();
  const displayName = user?.name || userId || '新同事';

  const [avatar, setAvatar] = useState(() => loadAvatar() || makeDefaultAvatar(displayName));
  const [hasSavedAvatar, setHasSavedAvatar] = useState(() => !!loadAvatar());
  const [showCustomizer, setShowCustomizer] = useState(() => !loadAvatar());
  const [showTownBoard, setShowTownBoard] = useState(false);
  const boardAutoOpenedRef = useRef(false);
  const [lastProjectRecord, setLastProjectRecord] = useState(loadTownContinuation);
  const [projects, setProjects] = useState(null); // null = 加载中
  const [projectsUserId, setProjectsUserId] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await authenticatedFetch(`${API_HOST}/api/project/list`, {
          headers: { 'X-User-ID': userId },
        });
        if (cancelled) return;
        setProjects(Array.isArray(list) ? list : []);
        setProjectsUserId(userId);
        setLoadFailed(false);
      } catch (e) {
        if (cancelled) return;
        console.error('[pixel-office] 项目列表加载失败', e);
        setLoadFailed(true);
        setProjects([]); // 失败仍展示空大厅
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const scene = useMemo(() => (projects === null ? null : buildLobbyScene(projects)), [projects]);

  const continuation = useMemo(() => resolveTownContinuation(
    lastProjectRecord, projectsUserId === userId ? projects : null,
    (project) => !isInteractiveGameProject(project),
  ), [projects, projectsUserId, userId, lastProjectRecord]);
  const continuationLabel = continuation ? getTownContinuationLabel(continuation) : '';

  useEffect(() => {
    if (projects === null || showCustomizer || boardAutoOpenedRef.current) return;
    try {
      if (localStorage.getItem('pixel-town:board-seen') !== 'yes') {
        boardAutoOpenedRef.current = true;
        setShowTownBoard(true);
        localStorage.setItem('pixel-town:board-seen', 'yes');
      }
    } catch { /* 无本地存储权限时不自动弹出 */ }
  }, [projects, showCustomizer]);

  const welcomeToasts = useMemo(() => {
    const count = Array.isArray(projects) ? projects.length : 0;
    return [
      count > 0 ? `欢迎回来，小镇里有 ${count} 个项目工位岛` : '欢迎来到漫剧小镇，先去列表创建第一部项目吧',
      '点击地面移动；靠近同事、公告栏或门垫都可以交互',
    ];
  }, [projects]);

  const openProject = useCallback((project) => {
    if (!project?.id) return;
    const record = createTownProjectVisit(
      loadTownContinuation(), project, Date.now(), !isInteractiveGameProject(project),
    );
    if (!record) return;
    saveTownContinuation(record);
    setLastProjectRecord(record);
    setShowTownBoard(false);
    const entryPath = isInteractiveGameProject(project)
      ? getProjectEntryPath(project)
      : `/project/${project.id}/office`;
    navigate(entryPath, { state: { projectName: record.name } });
  }, [navigate]);

  const continueWorking = useCallback(() => {
    if (!continuation) return;
    const { project, target } = continuation;
    if (!target) {
      openProject(project);
      return;
    }
    setShowTownBoard(false);
    navigate(target.route, { state: {
      projectName: project.name || project.title || '项目空间',
      episodeLabel: target.episodeLabel,
    } });
  }, [continuation, navigate, openProject]);

  const handleAction = useCallback((action) => {
    if (!action) return;
    if (action.type === 'route') {
      const match = String(action.path || '').match(/^\/project\/([^/]+)\/office$/);
      const project = match && Array.isArray(projects)
        ? projects.find((item) => String(item.id) === String(match[1]))
        : null;
      if (project) {
        openProject(project);
        return;
      }
      navigate(action.path, action.state ? { state: action.state } : undefined);
    } else if (action.type === 'custom' && action.id === 'dressing') {
      setShowCustomizer(true);
    } else if (action.type === 'custom' && action.id === 'town-board') {
      setShowTownBoard(true);
    }
  }, [navigate, openProject, projects]);

  const switchToList = () => {
    localStorage.setItem('home_mode', 'list');
    navigate(0);
  };

  const handleSaveAvatar = (next) => {
    setAvatar(next);
    setHasSavedAvatar(true);
    setShowCustomizer(false);
  };

  return (
    <div className="pixel-town-page pixel-town-page--standalone">
      <div className="pixel-town-toolbar">
        <div className="pixel-town-toolbar__identity">
          <span className="pixel-town-toolbar__mark"><MapPinned size={15} /></span>
          <span className="pixel-town-toolbar__title">漫剧小镇</span>
          {projects !== null && (
            <span className="pixel-town-toolbar__meta">
              {projects.length} 个项目
            </span>
          )}
        </div>
        <div className="pixel-town-toolbar__actions">
          <Link to="/annual-recap" className="pixel-town-button min-h-11 min-w-11 focus-visible:outline-2 focus-visible:outline-offset-2" title="查看我的年度创作回响">
            <Sparkles size={15} aria-hidden="true" />
            <span className="pixel-town-button__label">年度回响</span>
          </Link>
          <OperationsEntryLink className="pixel-town-button min-h-11 min-w-11 focus-visible:outline-2 focus-visible:outline-offset-2" labelClassName="pixel-town-button__label" />
          {continuation && (
            <button
              type="button"
              onClick={continueWorking}
              className="pixel-town-button max-w-52"
              title={`${continuationLabel} · 《${continuation.project.name || continuation.project.title || '未命名项目'}》`}
            >
              <History size={13} />
              <span className="pixel-town-button__label pixel-town-button__label--secondary truncate">{continuationLabel}</span>
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowTownBoard(true)}
            className="pixel-town-button"
            title="查看今日片场"
          >
            <ClipboardList size={13} /> <span className="pixel-town-button__label">今日片场</span>
          </button>
          <button
            type="button"
            onClick={switchToList}
            className="pixel-town-button"
            title="切换回项目列表首页"
          >
            <List size={13} /> <span className="pixel-town-button__label">项目列表</span>
          </button>
        </div>
      </div>

      {/* 舞台 */}
      <div className="pixel-town-stage-shell">
        {scene === null ? (
          <PixelSkeleton />
        ) : (
          <PixelStage
            scene={scene}
            avatar={avatar}
            paused={showCustomizer || showTownBoard}
            welcomeToasts={welcomeToasts}
            onAction={handleAction}
            onCustomizeAvatar={() => setShowCustomizer(true)}
          />
        )}

        {loadFailed && scene !== null && (
          <div className="pixel-town-error-note">
            项目列表加载失败，先随便逛逛吧
          </div>
        )}

        {showCustomizer && (
          <AvatarCustomizer
            initialAvatar={hasSavedAvatar ? avatar : null}
            defaultName={displayName}
            allowCancel={hasSavedAvatar}
            onSave={handleSaveAvatar}
            onCancel={() => setShowCustomizer(false)}
          />
        )}

        {showTownBoard && projects !== null && (
          <TownBoardPanel
            projects={projects}
            onClose={() => setShowTownBoard(false)}
            onOpenProject={openProject}
            onSwitchToList={switchToList}
          />
        )}
      </div>
    </div>
  );
}
